import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../api/axiosInstance';
import PageHeader from '../../components/common/PageHeader';

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatBytes(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = n;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatUptime(value) {
  const totalSeconds = Number(value || 0);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '-';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days} j ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}

function formatPercent(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '-';
  return `${n.toFixed(1)} %`;
}

function formatIps(ips) {
  if (!Array.isArray(ips) || ips.length === 0) return [];
  return ips.map((ip) => String(ip || '').trim()).filter(Boolean);
}

function normalizeIpValue(value) {
  return String(value || '').trim().replace(/^::ffff:/, '').split('/')[0];
}

function isUsefulLocalIp(ip) {
  const value = normalizeIpValue(ip);
  return value && !value.includes(':') && !value.startsWith('127.') && value !== '0.0.0.0';
}

function getAgentLocalIps(agent) {
  const networks = Array.isArray(agent.lastInventory?.network) ? agent.lastInventory.network : [];
  const values = [];
  networks.forEach((network) => {
    (Array.isArray(network.ips) ? network.ips : []).forEach((ip) => {
      const value = normalizeIpValue(ip);
      if (isUsefulLocalIp(value) && !values.includes(value)) values.push(value);
    });
  });
  const withoutApipa = values.filter((ip) => !ip.startsWith('169.254.'));
  if (withoutApipa.length > 0) return withoutApipa;
  if (values.length > 0) return values;
  return agent.ipAddress ? [normalizeIpValue(agent.ipAddress)] : [];
}

function DetailRow({ label, value, mono = false }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" component={mono ? 'code' : 'span'} sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{value || '-'}</Typography>
    </Stack>
  );
}

function AgentInventoryPanel({ agent }) {
  const inventory = agent.lastInventory || {};
  const hardware = inventory.hardware || {};
  const updates = inventory.updates || {};
  const updateItems = Array.isArray(updates.items) ? updates.items : [];
  const disks = Array.isArray(inventory.disks) ? inventory.disks : [];
  const networks = Array.isArray(inventory.network) ? inventory.network : [];

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Matériel</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <DetailRow label="Vendor" value={hardware.vendor || hardware.manufacturer || '-'} />
              <DetailRow label="Serial number" value={hardware.serialNumber || '-'} mono />
              <DetailRow label="Modèle" value={hardware.model || hardware.productName || '-'} />
              <DetailRow label="Produit" value={hardware.productName || '-'} />
            </Stack>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Mises à jour</Typography>
              <Stack direction="row" spacing={0.75}>
                <Chip size="small" color={Number(updates.pendingCount || 0) > 0 ? 'warning' : 'success'} variant={Number(updates.pendingCount || 0) > 0 ? 'filled' : 'outlined'} label={`${Number(updates.pendingCount || 0)} à faire`} />
                {updates.source ? <Chip size="small" variant="outlined" label={updates.source} /> : null}
              </Stack>
            </Stack>
            {Array.isArray(updates.warnings) && updates.warnings.length > 0 ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>{updates.warnings.join(' | ')}</Alert>
            ) : null}
            {updateItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Aucune mise à jour détaillée remontée.</Typography>
            ) : (
              <Stack spacing={0.75} sx={{ maxHeight: 260, overflow: 'auto', pr: 0.5 }}>
                {updateItems.map((item, index) => (
                  <Paper key={`${item.name || 'update'}-${index}`} variant="outlined" sx={{ p: 1, borderRadius: 1.5, bgcolor: 'background.paper' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{item.name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                          {[item.currentVersion ? `Actuelle: ${item.currentVersion}` : '', item.version ? `Cible: ${item.version}` : '', item.repository || '', item.kb || '', item.severity || ''].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      {item.kb ? <Chip size="small" variant="outlined" label={item.kb} /> : null}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Disques</Typography>
                <Chip size="small" variant="outlined" label={`${disks.length} disque(s)`} />
              </Stack>
              {disks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune donnée disque remontée.</Typography>
              ) : disks.map((disk, index) => {
                const total = Number(disk.totalBytes || 0);
                const free = Number(disk.freeBytes || 0);
                const used = Math.max(total - free, 0);
                const percent = Number(disk.usedPercent || (total > 0 ? (used / total) * 100 : 0));
                return (
                  <Paper key={`${disk.mount || disk.name || 'disk'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper' }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{disk.mount || disk.name || `Disque ${index + 1}`}</Typography>
                        <Chip size="small" label={formatPercent(percent)} color={percent >= 90 ? 'error' : percent >= 75 ? 'warning' : 'success'} variant="outlined" />
                      </Stack>
                      <Box sx={{ height: 7, borderRadius: 999, bgcolor: 'grey.200', overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(Math.max(percent, 0), 100)}%`, height: '100%', bgcolor: percent >= 90 ? 'error.main' : percent >= 75 ? 'warning.main' : 'success.main' }} />
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                        <DetailRow label="Utilisé / total" value={`${formatBytes(used)} / ${formatBytes(total)}`} />
                        <DetailRow label="Libre" value={formatBytes(free)} />
                        <DetailRow label="FS" value={disk.filesystem || '-'} />
                        <DetailRow label="Nom" value={disk.name || '-'} mono />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Réseau</Typography>
                <Chip size="small" variant="outlined" label={`${networks.length} carte(s)`} />
              </Stack>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <DetailRow label="IP publique/API" value={agent.lastRemoteAddress || '-'} mono />
                <DetailRow label="IP principale" value={agent.ipAddress || '-'} mono />
              </Stack>
              {networks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune donnée réseau remontée.</Typography>
              ) : networks.map((network, index) => {
                const ips = formatIps(network.ips);
                return (
                  <Paper key={`${network.name || 'net'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper' }}>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{network.name || `Carte ${index + 1}`}</Typography>
                        <Typography variant="caption" component="code" sx={{ color: 'text.secondary' }}>{network.mac || 'MAC -'}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {ips.length ? ips.map((ip) => <Chip key={ip} size="small" variant="outlined" label={ip} />) : <Chip size="small" variant="outlined" label="Aucune IP" />}
                      </Stack>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        <DetailRow label="Reçu" value={formatBytes(network.rxBytes)} />
                        <DetailRow label="Envoyé" value={formatBytes(network.txBytes)} />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        </Stack>

      </Stack>
    </Box>
  );
}

function getStatusColor(status) {
  if (status === 'online') return 'success';
  if (status === 'offline') return 'error';
  return 'default';
}

function getUpdateStatusMeta(status) {
  const normalized = status || 'idle';
  if (normalized === 'success' || normalized === 'up_to_date') return { label: 'Success', color: 'success' };
  if (normalized === 'failed') return { label: 'Failed', color: 'error' };
  if (['pending', 'acknowledged', 'checking', 'restarting'].includes(normalized)) return { label: 'In progress', color: 'warning' };
  return { label: '-', color: 'default' };
}

function getStepStatusLabel(status) {
  const normalized = status || 'idle';
  if (normalized === 'pending') return 'En attente';
  if (normalized === 'acknowledged') return 'Recu';
  if (normalized === 'checking') return 'Verification';
  if (normalized === 'restarting') return 'Redemarrage';
  if (normalized === 'success') return 'Succes';
  if (normalized === 'failed') return 'Erreur';
  if (normalized === 'up_to_date') return 'A jour';
  return normalized || '-';
}

function getRebootStatusMeta(status) {
  const normalized = status || 'idle';
  if (normalized === 'success') return { label: 'Reboot OK', color: 'success' };
  if (normalized === 'failed') return { label: 'Reboot failed', color: 'error' };
  if (['pending', 'acknowledged', 'rebooting'].includes(normalized)) return { label: 'Rebooting', color: 'warning' };
  return { label: '-', color: 'default' };
}

function getUpdateTooltip(agent) {
  const parts = [];
  if (agent.updateRequestedAt) parts.push(`Demandee: ${formatDateTime(agent.updateRequestedAt)}`);
  if (agent.updateStartedAt) parts.push(`Demarree: ${formatDateTime(agent.updateStartedAt)}`);
  if (agent.updateCompletedAt) parts.push(`Terminee: ${formatDateTime(agent.updateCompletedAt)}`);
  if (agent.updateTargetVersion) parts.push(`Cible: ${agent.updateTargetVersion}`);
  if (agent.updateMessage) parts.push(agent.updateMessage);
  if (agent.updateError) parts.push(`Erreur: ${agent.updateError}`);
  return parts.join(' | ') || 'Aucune mise a jour demandee';
}

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [tokenDraft, setTokenDraft] = useState({ name: '', organizationId: '', site: '', tags: '' });
  const [deployment, setDeployment] = useState({
    organizationId: '',
    tokenId: '',
    platform: 'windows',
    serverUrl: process.env.REACT_APP_API_URL || 'https://api.omnibox.re',
    site: '',
    tags: '',
  });
  const [newToken, setNewToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [updateDetailsAgent, setUpdateDetailsAgent] = useState(null);
  const [rebootAgent, setRebootAgent] = useState(null);
  const [rebootConfirmText, setRebootConfirmText] = useState('');
  const [deleteAgent, setDeleteAgent] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError('');
    try {
      const [agentsRes, tokensRes, orgsRes] = await Promise.all([
        axios.get('/api/agents'),
        axios.get('/api/agents/enrollment-tokens'),
        axios.get('/api/agents/organizations'),
      ]);
      setAgents(Array.isArray(agentsRes.data?.data) ? agentsRes.data.data : []);
      setTokens(Array.isArray(tokensRes.data?.data) ? tokensRes.data.data : []);
      setOrganizations(Array.isArray(orgsRes.data?.data) ? orgsRes.data.data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de charger les agents.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = window.setInterval(() => {
      loadData(true);
    }, 30000);
    return () => window.clearInterval(interval);
  }, [loadData]);

  const createToken = async () => {
    setError('');
    setMessage('');
    try {
      const payload = {
        name: tokenDraft.name,
        organizationId: tokenDraft.organizationId || null,
        site: tokenDraft.site,
        tags: tokenDraft.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      };
      const { data } = await axios.post('/api/agents/enrollment-tokens', payload);
      setNewToken(data.token || '');
      setMessage(data.message || 'Token créé.');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de créer le token.');
    }
  };

  const revokeToken = useCallback(async (id) => {
    setError('');
    try {
      await axios.delete(`/api/agents/enrollment-tokens/${id}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de révoquer le token.');
    }
  }, [loadData]);

  const copyToken = async () => {
    if (!newToken) return;
    await navigator.clipboard?.writeText(newToken);
    setMessage('Token copié dans le presse-papiers.');
  };


  const activeDeploymentTokens = useMemo(() => tokens.filter((token) => (
    token.enabled
    && !token.revokedAt
    && token.tokenValue
    && (!deployment.organizationId || token.Organization?._id === deployment.organizationId)
  )), [deployment.organizationId, tokens]);

  const selectedDeploymentToken = useMemo(() => {
    if (deployment.tokenId) {
      return activeDeploymentTokens.find((token) => token._id === deployment.tokenId) || null;
    }
    return activeDeploymentTokens[0] || null;
  }, [activeDeploymentTokens, deployment.tokenId]);

  const selectedDeploymentOrg = useMemo(
    () => organizations.find((org) => org._id === deployment.organizationId) || null,
    [deployment.organizationId, organizations]
  );

  const deploymentCommand = useMemo(() => {
    const enrollmentToken = selectedDeploymentToken?.tokenValue || '';
    const serverUrl = (deployment.serverUrl || 'https://api.omnibox.re').replace(/\/$/, '');
    const site = deployment.site || selectedDeploymentToken?.site || selectedDeploymentOrg?.name || '';
    const tags = deployment.tags || 'production';

    if (!enrollmentToken) return '';

    if (deployment.platform === 'linux') {
      const downloadUrl = `${serverUrl}/api/agents/download/linux`;
      return `curl -fsSL -H "x-enrollment-token: ${enrollmentToken}" "${downloadUrl}" -o /tmp/sagent-linux-amd64 && chmod +x /tmp/sagent-linux-amd64 && sudo /tmp/sagent-linux-amd64 install --server-url "${serverUrl}" --enrollment-token "${enrollmentToken}" --site "${site}" --tags "${tags}"`;
    }

    const downloadUrl = `${serverUrl}/api/agents/download/windows`;
    return String.raw`New-Item -ItemType Directory -Force -Path C:\ProgramData\SAgent | Out-Null; Invoke-WebRequest -Uri "${downloadUrl}" -Headers @{ "x-enrollment-token" = "${enrollmentToken}" } -OutFile "C:\ProgramData\SAgent\sagent.exe"; & "C:\ProgramData\SAgent\sagent.exe" install --server-url "${serverUrl}" --enrollment-token "${enrollmentToken}" --site "${site}" --tags "${tags}"`;
  }, [deployment.platform, deployment.serverUrl, deployment.site, deployment.tags, selectedDeploymentOrg, selectedDeploymentToken]);

  const copyDeploymentCommand = async () => {
    if (!deploymentCommand) return;
    await navigator.clipboard?.writeText(deploymentCommand);
    setMessage('Commande de déploiement copiée.');
  };

  const downloadAgent = async () => {
    const platform = deployment.platform === 'linux' ? 'linux' : 'windows';
    const fileName = platform === 'linux' ? 'sagent-linux-amd64' : 'sagent.exe';
    const enrollmentToken = selectedDeploymentToken?.tokenValue || '';
    if (!enrollmentToken) {
      setError('Aucun token d enrôlement actif pour télécharger l agent.');
      return;
    }
    setError('');
    try {
      const response = await axios.get(`/api/agents/download/${platform}`, {
        responseType: 'blob',
        headers: { 'x-enrollment-token': enrollmentToken },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de télécharger l agent.');
    }
  };


  const requestAgentUpdate = useCallback(async (agentId) => {
    if (!agentId) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`/api/agents/${agentId}/request-update`);
      setMessage(data.message || 'Mise à jour demandée.');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander la mise à jour.');
    }
  }, [loadData]);

  const requestAgentReboot = useCallback(async () => {
    if (!rebootAgent?.agentId) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`/api/agents/${rebootAgent.agentId}/request-reboot`);
      setMessage(data.message || 'Redémarrage demandé.');
      setRebootAgent(null);
      setRebootConfirmText('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander le redémarrage.');
    }
  }, [loadData, rebootAgent]);

  const requestAgentDelete = useCallback(async () => {
    if (!deleteAgent?.agentId) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.delete(`/api/agents/${deleteAgent.agentId}`);
      setMessage(data.message || 'Agent supprimé.');
      setDeleteAgent(null);
      setDeleteConfirmText('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de supprimer l agent.');
    }
  }, [deleteAgent, loadData]);

  const agentColumns = useMemo(() => [
    {
      accessorKey: 'computedStatus',
      header: 'Statut',
      size: 110,
      Cell: ({ row }) => <Chip size="small" color={getStatusColor(row.original.computedStatus)} label={row.original.computedStatus || 'unknown'} />,
    },
    { accessorKey: 'hostname', header: 'Hostname', size: 190 },
    {
      accessorKey: 'Organization.name',
      header: 'Client',
      size: 220,
      Cell: ({ row }) => row.original.Organization?.name || '-',
    },
    { accessorKey: 'site', header: 'Site', size: 130 },
    {
      accessorKey: 'os.name',
      header: 'OS',
      size: 250,
      Cell: ({ row }) => [row.original.os?.name, row.original.os?.version, row.original.os?.arch].filter(Boolean).join(' ') || '-',
    },
    {
      accessorKey: 'lastInventory.hardware.vendor',
      header: 'Vendor',
      size: 150,
      Cell: ({ row }) => row.original.lastInventory?.hardware?.vendor || row.original.lastInventory?.hardware?.manufacturer || '-',
    },
    {
      accessorKey: 'lastInventory.hardware.model',
      header: 'Modèle',
      size: 170,
      Cell: ({ row }) => row.original.lastInventory?.hardware?.model || row.original.lastInventory?.hardware?.productName || '-',
    },
    {
      accessorKey: 'lastInventory.hardware.serialNumber',
      header: 'Serial',
      size: 150,
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      id: 'localIps',
      header: 'IP locales',
      size: 240,
      accessorFn: (row) => getAgentLocalIps(row).join(' '),
      Cell: ({ row }) => {
        const ips = getAgentLocalIps(row.original);
        return ips.length ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {ips.slice(0, 5).map((ip) => <Chip key={ip} size="small" variant="outlined" label={ip} />)}
            {ips.length > 5 ? <Chip size="small" variant="outlined" label={`+${ips.length - 5}`} /> : null}
          </Stack>
        ) : '-';
      },
    },
    { accessorKey: 'lastRemoteAddress', header: 'IP publique', size: 140 },
    {
      accessorKey: 'lastUptimeSeconds',
      header: 'Uptime',
      size: 120,
      Cell: ({ cell }) => formatUptime(cell.getValue()),
    },
    {
      accessorKey: 'lastInventory.updates.pendingCount',
      header: 'MAJ',
      size: 110,
      Cell: ({ row }) => {
        const updates = row.original.lastInventory?.updates || {};
        const count = Number(updates.pendingCount ?? 0);
        const hasWarnings = Array.isArray(updates.warnings) && updates.warnings.length > 0;
        return (
          <Tooltip title={hasWarnings ? updates.warnings.join(' | ') : updates.source || ''}>
            <Chip
              size="small"
              color={count > 0 ? 'warning' : hasWarnings ? 'default' : 'success'}
              variant={count > 0 ? 'filled' : 'outlined'}
              label={hasWarnings && !count ? 'N/A' : `${count} MAJ`}
            />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'lastMetrics.cpu.usagePercent',
      header: 'CPU',
      size: 120,
      Cell: ({ row }) => row.original.lastMetrics?.cpu?.usagePercent != null ? `${Number(row.original.lastMetrics.cpu.usagePercent).toFixed(1)} %` : '-',
    },
    {
      accessorKey: 'lastMetrics.memory.usedBytes',
      header: 'RAM',
      size: 150,
      Cell: ({ row }) => {
        const memory = row.original.lastMetrics?.memory || row.original.lastInventory?.memory || {};
        return memory.usedBytes ? `${formatBytes(memory.usedBytes)} / ${formatBytes(memory.totalBytes)}` : formatBytes(memory.totalBytes);
      },
    },
    {
      accessorKey: 'lastSeenAt',
      header: 'Dernière remontée',
      size: 190,
      Cell: ({ cell }) => formatDateTime(cell.getValue()),
    },
    {
      accessorKey: 'agentVersion',
      header: 'Version',
      size: 100,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{cell.getValue() || '-'}</Typography>,
    },
    {
      accessorKey: 'updateStatus',
      header: 'Update',
      size: 170,
      Cell: ({ row }) => {
        const status = row.original.updateStatus || 'idle';
        const hasProcess = ['pending', 'acknowledged', 'checking', 'restarting', 'success', 'failed', 'up_to_date'].includes(status);
        const hasUpdate = Boolean(row.original.agentUpdateAvailable);
        const latest = row.original.latestAgentVersion || '-';
        const meta = hasProcess ? getUpdateStatusMeta(status) : hasUpdate ? { label: `MAJ ${latest}`, color: 'warning' } : { label: 'A jour', color: 'success' };
        const tooltip = hasProcess ? getUpdateTooltip(row.original) : hasUpdate ? `Dernière version disponible: ${latest}` : `Dernière version: ${latest}`;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={tooltip}>
              <Chip size="small" color={meta.color} variant={hasUpdate || hasProcess ? 'filled' : 'outlined'} label={meta.label} />
            </Tooltip>
            <Tooltip title="Voir les étapes">
              <span>
                <IconButton size="small" onClick={() => setUpdateDetailsAgent(row.original)}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      id: 'agentActions',
      header: 'Actions',
      size: 150,
      minSize: 140,
      maxSize: 170,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const canUpdate = Boolean(row.original.agentUpdateAvailable);
        const title = canUpdate
          ? row.original.updateRequestedAt
            ? `Demandée le ${formatDateTime(row.original.updateRequestedAt)}`
            : `Mettre à jour vers ${row.original.latestAgentVersion || 'la dernière version'}`
          : 'Agent déjà à jour';
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 132, whiteSpace: 'nowrap' }}>
            <Tooltip title={title}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!canUpdate}
                  onClick={() => requestAgentUpdate(row.original.agentId)}
                >
                  <SystemUpdateAltRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redémarrer le device">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={row.original.computedStatus !== 'online'}
                  onClick={() => { setRebootAgent(row.original); setRebootConfirmText(''); }}
                >
                  <RestartAltRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Supprimer de la liste">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => { setDeleteAgent(row.original); setDeleteConfirmText(''); }}
                >
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ], [requestAgentUpdate]);

  const tokenColumns = useMemo(() => [
    { accessorKey: 'name', header: 'Nom', size: 180 },
    {
      accessorKey: 'Organization.name',
      header: 'Client',
      size: 220,
      Cell: ({ row }) => row.original.Organization?.name || '-',
    },
    { accessorKey: 'site', header: 'Site', size: 120 },
    {
      accessorKey: 'enabled',
      header: 'Actif',
      size: 100,
      Cell: ({ row }) => <Chip size="small" color={row.original.enabled && !row.original.revokedAt ? 'success' : 'default'} label={row.original.enabled && !row.original.revokedAt ? 'Oui' : 'Non'} />,
    },
    { accessorKey: 'tokenPrefix', header: 'Préfixe', size: 120 },
    {
      accessorKey: 'tokenValue',
      header: 'Token',
      size: 320,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const token = row.original.tokenValue || '';
        if (!token) return <Typography variant="caption" color="text.secondary">Non disponible</Typography>;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={token}>
              <Box component="code" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: 12 }}>
                {token}
              </Box>
            </Tooltip>
            <Tooltip title="Copier">
              <IconButton size="small" onClick={() => navigator.clipboard?.writeText(token)}>
                <ContentCopyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    { accessorKey: 'useCount', header: 'Usages', size: 90 },
    {
      accessorKey: 'lastUsedAt',
      header: 'Dernier usage',
      size: 180,
      Cell: ({ cell }) => formatDateTime(cell.getValue()),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 90,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Tooltip title="Révoquer">
          <span>
            <IconButton size="small" color="error" disabled={Boolean(row.original.revokedAt)} onClick={() => revokeToken(row.original._id)}>
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ], [revokeToken]);

  const agentTable = useMaterialReactTable({
    columns: agentColumns,
    data: agents,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: 'compact',
      sorting: [{ id: 'hostname', desc: false }],
      pagination: { pageIndex: 0, pageSize: 25 },
      columnVisibility: {
        site: false,
        'lastInventory.hardware.vendor': false,
        'lastInventory.hardware.model': false,
        'lastInventory.hardware.serialNumber': false,
        lastRemoteAddress: false,
        'lastMetrics.cpu.usagePercent': false,
        'lastMetrics.memory.usedBytes': false,
        lastSeenAt: false,
      },
    },
    state: { isLoading: loading },
    getRowId: (row) => row.agentId,
    enableExpanding: true,
    positionExpandColumn: 'first',
    renderDetailPanel: ({ row }) => <AgentInventoryPanel agent={row.original} />,
  });

  const tokenTable = useMaterialReactTable({
    columns: tokenColumns,
    data: tokens,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    initialState: { showColumnFilters: true, showGlobalFilter: true, density: 'compact', pagination: { pageIndex: 0, pageSize: 10 } },
    state: { isLoading: loading },
    getRowId: (row) => row._id,
  });

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <PageHeader section="Infrastructure" title="Agents" sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadData} disabled={loading}>Actualiser</Button>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setNewToken(''); setTokenDialogOpen(true); }}>Créer un token</Button>
          </Stack>
        </Stack>

        {message ? <Alert severity="success">{message}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${agents.length} agent(s)`} />
          <Chip color="success" variant="outlined" label={`${agents.filter((agent) => agent.computedStatus === 'online').length} online`} />
          <Chip color="error" variant="outlined" label={`${agents.filter((agent) => agent.computedStatus === 'offline').length} offline`} />
          <Chip variant="outlined" label={`${tokens.filter((token) => token.enabled && !token.revokedAt).length} token(s) actif(s)`} />
        </Stack>


        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Déployer un agent</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sélectionne le client puis copie la commande sur la machine. Elle télécharge l agent avec le token d enrôlement puis l installe automatiquement.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={downloadAgent}>Télécharger</Button>
                <Button variant="contained" startIcon={<ContentCopyRoundedIcon />} disabled={!deploymentCommand} onClick={copyDeploymentCommand}>Copier la commande</Button>
              </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                select
                label="Client"
                value={deployment.organizationId}
                onChange={(e) => setDeployment((draft) => ({ ...draft, organizationId: e.target.value, tokenId: '' }))}
                sx={{ minWidth: 260 }}
              >
                <MenuItem value="">Tous les clients</MenuItem>
                {organizations.map((org) => <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Token"
                value={selectedDeploymentToken?._id || ''}
                onChange={(e) => setDeployment((draft) => ({ ...draft, tokenId: e.target.value }))}
                sx={{ minWidth: 260 }}
              >
                {activeDeploymentTokens.length === 0 ? <MenuItem value="">Aucun token actif disponible</MenuItem> : null}
                {activeDeploymentTokens.map((token) => (
                  <MenuItem key={token._id} value={token._id}>{token.name || token.tokenPrefix} {token.site ? `- ${token.site}` : ''}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="OS"
                value={deployment.platform}
                onChange={(e) => setDeployment((draft) => ({ ...draft, platform: e.target.value }))}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="windows">Windows</MenuItem>
                <MenuItem value="linux">Linux</MenuItem>
              </TextField>
              <TextField
                label="URL serveur"
                value={deployment.serverUrl}
                onChange={(e) => setDeployment((draft) => ({ ...draft, serverUrl: e.target.value }))}
                sx={{ minWidth: 260 }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="Site" value={deployment.site} onChange={(e) => setDeployment((draft) => ({ ...draft, site: e.target.value }))} sx={{ minWidth: 220 }} />
              <TextField label="Tags" value={deployment.tags} onChange={(e) => setDeployment((draft) => ({ ...draft, tags: e.target.value }))} sx={{ minWidth: 260 }} helperText="Exemple : production,serveur" />
            </Stack>

            {!deploymentCommand ? (
              <Alert severity="info">Aucun token actif et lisible pour ce client. Crée un nouveau token si besoin.</Alert>
            ) : (
              <Box component="code" sx={{ display: 'block', p: 1.5, bgcolor: 'grey.100', borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>
                {deploymentCommand}
              </Box>
            )}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Machines supervisées</Typography>
            <MaterialReactTable table={agentTable} />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Tokens d'enrôlement</Typography>
            <MaterialReactTable table={tokenTable} />
          </Stack>
        </Paper>
      </Stack>

        <Dialog open={Boolean(updateDetailsAgent)} onClose={() => setUpdateDetailsAgent(null)} fullWidth maxWidth="sm">
          <DialogTitle>Étapes de mise à jour</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25}>
              <Typography variant="body2" color="text.secondary">
                {updateDetailsAgent?.hostname || '-'} - {updateDetailsAgent?.agentVersion || '-'} vers {updateDetailsAgent?.updateTargetVersion || updateDetailsAgent?.latestAgentVersion || '-'}
              </Typography>
              {(Array.isArray(updateDetailsAgent?.updateHistory) && updateDetailsAgent.updateHistory.length > 0 ? updateDetailsAgent.updateHistory : [{
                status: updateDetailsAgent?.updateStatus,
                message: updateDetailsAgent?.updateMessage,
                error: updateDetailsAgent?.updateError,
                currentVersion: updateDetailsAgent?.updateCurrentVersion,
                targetVersion: updateDetailsAgent?.updateTargetVersion,
                at: updateDetailsAgent?.lastUpdateCheckAt || updateDetailsAgent?.updateRequestedAt,
              }]).map((step, index) => {
                const meta = getUpdateStatusMeta(step.status);
                return (
                  <Paper key={`${step.status || 'step'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Chip size="small" color={meta.color} label={getStepStatusLabel(step.status)} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{step.message || '-'}</Typography>
                        {step.error ? <Typography variant="body2" color="error">{step.error}</Typography> : null}
                        <Typography variant="caption" color="text.secondary">
                          {[formatDateTime(step.at), step.currentVersion ? `Actuelle: ${step.currentVersion}` : '', step.targetVersion ? `Cible: ${step.targetVersion}` : ''].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUpdateDetailsAgent(null)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(deleteAgent)} onClose={() => setDeleteAgent(null)} fullWidth maxWidth="xs">
          <DialogTitle>Supprimer l agent</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Alert severity="error">Cette action retire {deleteAgent?.hostname || 'cet agent'} de la liste. Les historiques restent conservés.</Alert>
              <Typography variant="body2" color="text.secondary">Tape DELETE pour confirmer.</Typography>
              <TextField
                autoFocus
                label="Confirmation"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value)}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteAgent(null)}>Annuler</Button>
            <Button color="error" variant="contained" disabled={deleteConfirmText !== 'DELETE'} onClick={requestAgentDelete}>Supprimer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(rebootAgent)} onClose={() => setRebootAgent(null)} fullWidth maxWidth="xs">
          <DialogTitle>Confirmer le redémarrage</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Alert severity="warning">Cette action va redémarrer le device {rebootAgent?.hostname || ''}.</Alert>
              <Typography variant="body2" color="text.secondary">Tape REBOOT pour confirmer.</Typography>
              <TextField
                autoFocus
                label="Confirmation"
                value={rebootConfirmText}
                onChange={(event) => setRebootConfirmText(event.target.value)}
                fullWidth
              />
              {rebootAgent?.rebootStatus && rebootAgent.rebootStatus !== 'idle' ? (
                <Chip size="small" color={getRebootStatusMeta(rebootAgent.rebootStatus).color} label={getRebootStatusMeta(rebootAgent.rebootStatus).label} sx={{ alignSelf: 'flex-start' }} />
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRebootAgent(null)}>Annuler</Button>
            <Button color="warning" variant="contained" disabled={rebootConfirmText !== 'REBOOT'} onClick={requestAgentReboot}>Redémarrer</Button>
          </DialogActions>
        </Dialog>


      <Dialog open={tokenDialogOpen} onClose={() => setTokenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Créer un token d'enrôlement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nom" value={tokenDraft.name} onChange={(e) => setTokenDraft((draft) => ({ ...draft, name: e.target.value }))} fullWidth />
            <TextField select label="Client" value={tokenDraft.organizationId} onChange={(e) => setTokenDraft((draft) => ({ ...draft, organizationId: e.target.value }))} fullWidth>
              <MenuItem value="">Aucun client</MenuItem>
              {organizations.map((org) => <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>)}
            </TextField>
            <TextField label="Site" value={tokenDraft.site} onChange={(e) => setTokenDraft((draft) => ({ ...draft, site: e.target.value }))} fullWidth />
            <TextField label="Tags" helperText="Sépare les tags par des virgules." value={tokenDraft.tags} onChange={(e) => setTokenDraft((draft) => ({ ...draft, tags: e.target.value }))} fullWidth />
            {newToken ? (
              <Alert severity="warning">
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Token créé</Typography>
                  <Box component="code" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, wordBreak: 'break-all' }}>{newToken}</Box>
                  <Button size="small" startIcon={<ContentCopyRoundedIcon />} onClick={copyToken}>Copier le token</Button>
                </Stack>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenDialogOpen(false)}>Fermer</Button>
          <Button variant="contained" onClick={createToken}>Créer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
